// apps/app/src/components/common/page-error-boundary.tsx
//
// One broken page must not take the dashboard down with it. Without this, any
// render error unmounted the whole tree — rail, top bar and all — and left a
// blank screen with nothing to click. Moving to another page clears it.
//
// Reset by watching the path rather than by re-keying: a new key would remount
// the Suspense boundary inside, and a fresh boundary always shows its fallback —
// the very flash on navigation that transitions exist to avoid.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface State {
  error: Error | null;
}

export class PageErrorBoundary extends Component<{ children: ReactNode; path: string }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: { path: string }): void {
    if (this.state.error && previous.path !== this.props.path) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Page crashed', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return <PageError onRetry={() => this.setState({ error: null })} />;
  }
}

/** A function component, so the message can use the translation hook. */
function PageError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed bg-card px-6 py-16 text-center" role="alert">
      <span className="mb-3 grid size-10 place-items-center rounded-full bg-destructive/10 text-destructive">
        <TriangleAlert className="size-5" aria-hidden="true" />
      </span>
      <p className="text-sm font-medium">{t('pageError.title')}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t('pageError.body')}</p>
      <Button variant="outline" className="mt-4" onClick={onRetry}>
        {t('pageError.tryAgain')}
      </Button>
    </div>
  );
}
